<template>
    <LoadingView v-if="isLoading" />
    <div v-else class="flex flex-col gap-8">
        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton color="secondary" icon="fa-chevron-left"
                    @click="prev ? $router.push(prev) : $router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div class="py-4 sm:rounded-tr-lg flex justify-end items-center h-16 gap-2">
                <NotificationSubscription :type="NotificationTypes.DIVISION" :id="community.id" :context="community" />
                <ToggleButton v-if="user.level >= 2" id="publish-toggle" on-color="green"
                    :checked="community.status == 'active'" @changed="changeStatus" label="Set Publishing Status" />
            </div>
        </nav>
        <header class="flex items-start gap-8">
            <div class="flex flex-col lg:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-6 flex-1 justify-between">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">

                        <div class="flex-1 flex flex-col gap-2 lg:gap-0 order-2 lg:order-1">
                            <h1
                                class="text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
                                {{ community.meta?.info?.name || community.kova?.WebGroupName }}
                            </h1>
                            <dl class="lg:flex gap-2">
                                <dt class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    Kova Business Unit ID:
                                </dt>
                                <dd
                                    class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ community.bunit_rid }}
                                </dd>
                                <dt class="sr-only">
                                    Business Unit ID
                                </dt>
                                <dd
                                    class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ community.kova?.BUnitID }}
                                </dd>
                                <dt class="sr-only">
                                    Business Unit FID
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    {{ community.kova?.BUnitFID }}
                                </dd>

                            </dl>
                        </div>
                    </div>
                    <section class="flex flex-row w-full gap-2">
                        <h2 class="sr-only col-span-full">Basic Information</h2>
                        <InfoText class="w-full" label="Kova Status"
                            :icon="`fak ${!['Closed'].includes(community.kova?.Status) ? 'fa-bulb' : 'fa-bulb'}`"
                            :value="community.kova?.Status"
                            :color="!['Closed'].includes(community.kova?.Status) ? 'green' : 'red'"
                            :bg-color="!['Closed'].includes(community.kova?.Status) ? 'green' : 'red'" />
                        <InfoText class="w-full" bg-color="gray" label="Collections" icon="fak fa-collection"
                            :value="String(collections.length)" />
                        <InfoText class="w-full" bg-color="gray" label="Floorplans" icon="fak fa-floorplan"
                            :value="String(floorplans?.length)" />
                        <InfoText class="w-full" bg-color="gray" label="Available Homes" icon="fak fa-home-available"
                            :value="String(sales_orders?.length)" />
                    </section>

                </div>
                <div class="w-full lg:w-1/5 flex">
                    <img v-if="community.image" class="object-cove rounded-lg" :src="community.image"
                        :alt="community.kova?.Name" :title="community.kova?.Name">
                    <div v-else
                        class="text-5xl bg-bhav-blue-300 rounded-lg text-white p-8 flex items-center justify-center w-full">
                        <span class="fak fa-floorplan"></span>
                    </div>
                </div>

            </div>
        </header>
        <TabPanes route="community-tab" :tabs="tabs" v-slot="{ tab }" via="via-white" to="to-white" class="gap-4 py-6">
            <InfoPane :community="community" @update="prepare" v-if="tab?.name === 'info'" />
            <OfficePane :community="community" @update="prepare" v-if="tab?.name === 'office'" />
            <CollectionsPane :collections="collections" v-if="tab?.name === 'collections'" />
            <FloorplanPane :floorplans="floorplans" v-if="tab?.name === 'floorplans'" />
            <SalesOrderPane :sales_orders="sales_orders" :floorplans="floorplans" v-if="tab?.name === 'homes'" />
            <ImagePane :parent="community" context="division" @update="(image) => { community.image = image.media?.url }"
                v-if="tab?.name === 'photos'" />
            <VideoPane :item="community" context="community" @update="prepare" v-if="tab?.name === 'videos'" />
            <InteractivePane :item="community" context="community" @update="prepare"
                v-if="tab?.name === 'interactive'" />
            <AmenitiesPane :item="community" context="division" @update="prepare" v-if="tab?.name === 'amenities'" />
            <PromotionsPane context="divisions" :id="community.id" v-if="tab?.name === 'promotions'" />
            <HighlightsPane :community="community" @update="prepare" v-if="tab?.name === 'highlights'" />
            <LotsPane context="divisions" :id="community.id" :collections="collections" v-if="tab?.name === 'lots'" />
            <LogPane context="division" v-if="tab?.name === 'logs'" />

            <template v-if="tab?.name === 'debugger'">
                <h2>Raw Data</h2>
                <pre>
                    {{ community }}
                </pre>
            </template>
        </TabPanes>
    </div>
</template>

<script>
import axios from "axios";
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, ToggleButton } from '@/components/form';
import { InfoPane, OfficePane, CollectionsPane, HighlightsPane } from '@/components/communities';
import { InteractivePane, ImagePane, VideoPane, FloorplanPane, SalesOrderPane, LotsPane, LogPane, PromotionsPane } from '@/components/shared';
import { mapState, mapActions } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { useMessageStore } from "@/stores/message";
import community from '@/models/legacy/community';
import TabPanes from "@/components/TabPanes.vue";
import { handle404 } from "@/helpers/globals";
import Statuses from "@/enums/Statuses";
import NotificationTypes from "@/enums/NotificationTypes";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import { CommunitiesController } from "@/controllers/communities";
import InfoText from "@/components/media/InfoText.vue";
import AmenitiesPane from "@/components/shared/AmenitiesPane.vue";
import Community from "@/models/community";

let tabs = [
    {
        name: 'info',
        icon: 'fa-alert',
        title: 'General Information',
        acl: 0
    },
    {
        name: 'office',
        icon: 'fa-chevron',
        title: 'Sales Office',
        acl: 0
    },
    {
        name: 'collections',
        icon: 'fa-collection',
        title: 'Collections',
        acl: 0
    },
    {
        name: 'floorplans',
        icon: 'fa-floorplan',
        title: 'Floorplans',
        acl: 0
    },
    {
        name: 'homes',
        icon: 'fa-home-available',
        title: 'Available Homes',
        acl: 0
    },
    {
        name: 'photos',
        icon: 'fa-photo',
        title: 'Images',
        acl: 0
    },
    {
        name: 'videos',
        icon: 'fa-video',
        title: 'Videos',
        acl: 0
    },
    {
        name: 'interactive',
        icon: 'fa-3d',
        title: 'Interactives',
        acl: 0
    },
    {
        name: 'amenities',
        icon: 'fa-boat',
        title: 'Amenities',
        acl: 0
    },
    {
        name: 'highlights',
        icon: 'fa-community',
        title: 'Highlights',
        acl: 0
    },
    {
        name: 'promotions',
        icon: 'fa-dollar',
        title: 'Promotions',
        acl: 2
    },
    {
        name: 'lots',
        icon: 'fa-lot',
        title: 'Lots',
        acl: 0
    },
    {
        name: 'logs',
        icon: 'fa-history',
        title: 'History',
        acl: 1
    },
    {
        name: 'debugger',
        icon: 'fa-debug',
        title: 'Debugger',
        acl: 2
    },
];

export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.community?.meta?.info?.name}`
        }
    },
    setup() {
        return {
            NotificationTypes
        }
    },
    data() {
        return {
            community: new Community({id: this.$route.params.id}),
            collections: [],
            floorplans: [],
            sales_orders: [],
            isLoading: true,
            tabs: [],
            prev: false
        }
    },
    methods: {
        show() {
            CommunitiesController.show(this.community.id)
                .then(res => {
                    this.community = res;
                    this.isLoading = false;
                    this.indexCollections();
                    this.indexFloorplans();
                    this.indexSalesOrders();
                })
                .catch(err => {
                    if (err.status === 404)
                        handle404(this, 'The requested community could not be found');
                });
        },
        indexCollections() {
            axios.get(`divisions/${this.community.id}/collections`)
                .then(res => {
                    if (!res.data?.status)
                        this.collections = res.data;
                })
                .catch(e => {
                    console.error(e);
                })
        },
        async indexFloorplans() {
            await axios.get(`divisions/${this.community.id}/floorplans`)
                .then(res => {
                    if (!res.data?.status)
                        this.floorplans = res.data;
                })
                .catch(e => {
                    console.error(e);
                })
        },
        async indexSalesOrders() {
            await axios.get(`divisions/${this.community.id}/sales_orders`)
                .then(res => {
                    if (!res.data?.status)
                        this.sales_orders = res.data;
                })
                .catch(e => {
                    console.error(e);
                })
        },
        isGeoValueSet(value) {
            return value !== null && value !== undefined && value !== '' && value != '0';
        },
        changeStatus(toggle) {
            if (toggle) {
                const missingInfo = !this.isGeoValueSet(this.community.meta?.info?.geo?.lat) || !this.isGeoValueSet(this.community.meta?.info?.geo?.lng);
                const missingOffice = !this.isGeoValueSet(this.community.meta?.sales_office?.geo?.lat) || !this.isGeoValueSet(this.community.meta?.sales_office?.geo?.lng);

                if (missingInfo && missingOffice) {
                    this.setAlert({
                        text: 'Validation Error: Unable to publish. Geolocation coordinates (Latitude and Longitude) are missing for both the General Info and Sales Office locations.',
                        level: 'error'
                    });
                    return;
                }
                else if (missingInfo) {
                    this.setAlert({
                        text: 'Validation Error: Unable to publish. Latitude and Longitude coordinates are required for the General Info location.',
                        level: 'error'
                    });
                    return;
                }
                else if (missingOffice) {
                    this.setAlert({
                        text: 'Validation Error: Unable to publish. Latitude and Longitude coordinates are required for the Sales Office location.',
                        level: 'error'
                    });
                    return;
                }
            }
            this.community.status = toggle ? 'active' : 'inactive';
            this.save();
        },
        save() {
            this.isLoading = true;
            axios.patch(`divisions/${this.community.id}`, this.community)
                .catch(e => {
                    this.community = {...this.revert};
                    throw e;
                })
                .finally(() => {
                    this.revert = null;
                    this.isLoading = false;
                });
        },
        prepare(value) {
            this.revert = {...this.community};
            this.community = value;
            this.save();
        },
        ...mapActions(useMessageStore, ['setAlert'])
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        lot() {
            let lotId = this.community.kova?.Lot?.LotID;
            if (!lotId) return '-';
            return parseInt(lotId.split('-').pop());
        },
        statusLabel() {
            return Statuses.label(this.community.status)
        }
    },
    created() {
        this.show();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            if (!vm.prev) vm.prev = (from.name && from) ?? false;
        })
    },
    components: {
        NotificationSubscription,
        LoadingView,
        ToggleButton,
        FormButton,
        TabPanes,
        InteractivePane,
        VideoPane,
        CollectionsPane,
        FloorplanPane,
        SalesOrderPane,
        ImagePane,
        InfoPane,
        HighlightsPane,
        OfficePane,
        LotsPane,
        LogPane,
        PromotionsPane,
        InfoText,
        AmenitiesPane
    },
}
</script>